import '@/app/styles/components/Switch.css';
import React from 'react';
import classNames from 'classnames';

interface SwitchProps {
	/** Whether the switch is currently on/off */
	checked?: boolean;
	/** Callback function when switch state changes */
	onChange?: (checked: boolean) => void;
	/** Text to display when switch is ON */
	onText?: string;
	/** Text to display when switch is OFF */
	offText?: string;
	/** Icon to display when switch is ON (React node or string) */
	onIcon?: React.ReactNode;
	/** Icon to display when switch is OFF (React node or string) */
	offIcon?: React.ReactNode;
	/** Whether the switch is disabled */
	disabled?: boolean;
	/** Additional CSS classes */
	className?: string;
	/** Accessible label for screen readers */
	'aria-label'?: string;
}

export const Switch: React.FC<SwitchProps> = ({
	checked = false,
	onChange,
	onText = 'ON',
	offText = 'OFF',
	onIcon,
	offIcon,
	disabled = false,
	className = '',
	'aria-label': ariaLabel,
}) => {
	const handleToggle = () => {
		if (!disabled && onChange) {
			onChange(!checked);
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === ' ' || event.key === 'Enter') {
			event.preventDefault();
			handleToggle();
		}
	};

	return (
		<div
			className={classNames('switch', {
				'switch_checked': checked,
				'switch_unchecked': !checked,
				'switch_disabled': disabled,
			}, className)}
			onClick={handleToggle}
			onKeyDown={handleKeyDown}
			role="switch"
			aria-checked={checked}
			aria-label={ariaLabel}
			tabIndex={disabled ? -1 : 0}
		>
			{/* Track/Background */}
			<div className={classNames('switch__track', {
				'switch__track_checked': checked,
				'switch__track_unchecked': !checked,
			})}>

				{/* ON content - always on the left */}
				<div className="switch__content switch__content_on">
					{onIcon && (
						<span className="switch__icon">
							{onIcon}
						</span>
					)}
					{onText && (
						<span className="switch__text">
							{onText}
						</span>
					)}
				</div>

				{/* OFF content - always on the right */}
				<div className="switch__content switch__content_off">
					{offIcon && (
						<span className="switch__icon">
							{offIcon}
						</span>
					)}
					{offText && (
						<span className="switch__text">
							{offText}
						</span>
					)}
				</div>

				{/* Thumb/Slider - slides under the text/icon states */}
				<div className={classNames('switch__thumb', {
					'switch__thumb_checked': checked,
					'switch__thumb_unchecked': !checked,
				})}>
					<div className="switch__thumb-inner"></div>
				</div>
			</div>
		</div>
	);
};